/**
 * kombi/KombiModal.tsx — the Kombi modal: a panel that rises from the bottom of the screen.
 *
 * It IS the automation detail page's bottom drawer (`.catalogue-detail-drawer`, Kombi.css:6740) — same
 * geometry, same 600ms cubic-bezier(0.22, 1, 0.36, 1) rise, same paper-and-hairline treatment — with
 * the four things a form-bearing dialog needs and that drawer does not have. The detail page is
 * untouched and keeps its own class; see the CSS block for why each addition is load-bearing rather
 * than belt-and-braces.
 *
 * ── The focus contract, which is the whole reason this is a component and not just a class ─────
 * `useFocusTrap` (a11y.ts) gives Tab-cycling inside the dialog, Escape, and focus returning to the
 * control that opened it. That hook captures the opener during its FIRST RENDER, deliberately: a
 * dialog's own markup may move focus in a ref callback before effects run, and an effect-time read
 * would capture the dialog itself and then "restore" focus to a node about to be detached. So this
 * component must be MOUNTED ONLY WHILE OPEN — `{open && <KombiModal …>}` — rather than kept mounted
 * with `data-open` toggled. Rendering it permanently would capture the opener once, at page load,
 * when it was `<body>`.
 *
 * The consequence for the animation: mounting cannot also be the moment the transform runs, or the
 * panel would appear already-risen. `data-open` is therefore flipped on in a rAF after mount, which
 * is one frame of translateY(102%) and then the rise.
 *
 * That one frame is also why `.kombi-modal` carries NO `visibility: hidden`, unlike the drawer it is
 * built from. `.focus()` on a `visibility: hidden` element is a silent no-op, so with it the trap's
 * initial focus never landed and Tab went straight to the page behind — measured in a browser, not
 * theorised. Off-screen-but-visible is what the transform already provides.
 */
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { gsap } from 'gsap';
import { useFocusTrap } from '../a11y';

export interface KombiModalProps {
  /** The eyebrow above the title — what KIND of thing this is. */
  eyebrow?: string;
  title: string;
  /** One line under the title. Becomes the dialog's accessible description. */
  intro?: string;
  /** The dialog's own actions. Anything with `.kombi-modal-footer-left` is pushed to the left. */
  footer?: ReactNode;
  onClose: () => void;
  /**
   * Where focus goes on close. Optional: `useFocusTrap` already restores focus to whatever opened the
   * dialog, and this is only for the rare case where that is not the right landing spot.
   */
  returnFocusRef?: RefObject<HTMLElement | null>;
  children: ReactNode;
}

export function KombiModal({
  eyebrow,
  title,
  intro,
  footer,
  onClose,
  returnFocusRef,
  children,
}: KombiModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, onClose);

  // One frame at translateY(102%) so the rise is seen. See the header note on why mounting and
  // opening cannot be the same moment.
  const [risen, setRisen] = useState(false);
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setRisen(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  /**
   * THE CONTENT ENTRANCE, staged once the panel has ARRIVED rather than while it is still rising.
   *
   * `.catalogue-detail-drawer-inner` does this in CSS, with a 320ms `orientation-content-in` on mount.
   * On this modal that is the same moment the panel starts its 600ms rise, so the fade completed while
   * the content was still below the fold: it ran, and was never seen. Hence the delay, and hence gsap
   * rather than CSS — a stagger across the header, body and footer is the site's own motion language
   * (useKombiPageMotion uses the same power3.out), and CSS cannot express it without three delays.
   *
   * TWO PROPERTIES, THE SAME TWO THE PAGE MOTION KEEPS. The hidden state is set from JS, never CSS, so
   * a script that never runs leaves a fully visible modal rather than an empty one. And reduced motion
   * is a twin path that `set`s the final state — not a shortened duration, which would leave the
   * starting `autoAlpha: 0` in place and hide the content outright.
   */
  useEffect(() => {
    if (!risen) return;
    const root = dialogRef.current;
    if (!root) return;
    const parts = gsap.utils.toArray<HTMLElement>('[data-kombi-modal-in]', root);
    if (parts.length === 0) return;
    /*
      DELAYED BY THE PANEL'S OWN RISE, read off the element rather than hard-coded. The first version
      started the stagger the moment `risen` flipped — which is the same moment the CSS transform starts
      — so the content reached full opacity while the panel was still at translateY(30px) and the fade
      was over before it arrived. Measured: header opacity hit 1.00 at panelY=30.

      Reading `transitionDuration` keeps the timing in ONE place: Kombi.css owns the 600ms, and a magic
      number here would be a second copy of it that silently drifts the day the CSS changes.
    */
    const rise = parseFloat(getComputedStyle(root).transitionDuration) || 0;
    const context = gsap.context(() => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        gsap.set(parts, { autoAlpha: 1, y: 0 });
        return;
      }
      gsap.fromTo(
        parts,
        { autoAlpha: 0, y: 12 },
        {
          autoAlpha: 1,
          y: 0,
          // Slightly before the panel settles, so the two read as one movement rather than two.
          delay: Math.max(0, rise - 0.12),
          duration: 0.42,
          ease: 'power3.out',
          stagger: 0.07,
        },
      );
    }, root);
    return () => context.revert();
  }, [risen]);

  /**
   * SCROLL LOCK. Without it the page scrolls under an open dialog and the panel appears to drift.
   * The previous value is restored rather than assumed to be `''`, so a surface that had its own
   * overflow set does not silently lose it.
   */
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Focus goes wherever the caller asked, on top of the hook's own restore.
  useEffect(
    () => () => {
      const target = returnFocusRef?.current;
      if (target?.isConnected) target.focus();
    },
    [returnFocusRef],
  );

  const titleId = `kombi-modal-title-${title.replace(/\W+/g, '-').toLowerCase()}`;
  const introId = intro === undefined ? undefined : `${titleId}-intro`;

  return (
    <>
      {/* The scrim carries click-to-dismiss. `aria-hidden` because Escape and the close button are
          the accessible routes out; a screen reader has no use for a backdrop. */}
      <div
        aria-hidden="true"
        className="kombi-modal-scrim"
        data-open={risen}
        onMouseDown={onClose}
      />
      <div
        aria-labelledby={titleId}
        aria-modal="true"
        className="kombi-modal"
        data-open={risen}
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
        {...(introId === undefined ? {} : { 'aria-describedby': introId })}
      >
        <button className="kombi-modal-close" onClick={onClose} type="button">
          Close
        </button>
        <div className="kombi-modal-inner">
          <header className="kombi-modal-header" data-kombi-modal-in>
            {eyebrow !== undefined && <p className="kombi-modal-eyebrow">{eyebrow}</p>}
            <h2 id={titleId}>{title}</h2>
            {intro !== undefined && <p id={introId}>{intro}</p>}
          </header>
          <div className="kombi-modal-body" data-kombi-modal-in>
            {children}
          </div>
          {footer !== undefined && (
            <div className="kombi-modal-footer" data-kombi-modal-in>
              {footer}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
